/**
 * Subcard de uma AÇÃO dentro do card do plano (GUPM-332).
 *
 * É o antigo card de ação do `MpaActionList` sem o contexto herdado do plano
 * (barragem, nível, TARP, instrumento) — esse contexto passou a ser exibido uma
 * única vez, no cabeçalho do card do plano. Aqui fica só o que varia de ação
 * para ação: descrição, status, responsável, prazo e o avanço de status.
 *
 * O `data-vencido` continua neste nível: é o elemento que contém a descrição, e
 * é por ele que a UI (e o guard de teste `prazo-vencido-em-vermelho`) identifica
 * a ação vencida.
 */
import { Button } from '@/ui';
import { type AcaoStatus } from '@/domain/planos';
import { acaoVencida, type AcaoFlat } from '@/domain/planos/mpa';
import { formatarDiaBr, formatarDiaDoInstante } from '@/domain/fuso-brasilia';

const ACAO_STATUS_COLOR: Record<AcaoStatus, string> = {
  pendente: '#f97316',
  andamento: '#78B4CF',
  concluido: '#22c55e',
};

// Rótulo compacto do subcard (singular) — o plural "Em andamento" fica exclusivo
// da pill global do topo, garantindo unicidade de texto para os testes.
const ACAO_STATUS_CARD: Record<AcaoStatus, string> = {
  pendente: 'Pendente',
  andamento: 'Andamento',
  concluido: 'Concluído',
};


interface Props {
  acao: AcaoFlat;
  hoje: Date;
  canEdit: boolean;
  updating: boolean;
  onUpdateAcao: (planoId: string, acaoId: string, status: AcaoStatus) => void;
}

export function MpaAcaoSubcard({ acao, hoje, canEdit, updating, onUpdateAcao }: Props) {
  const vencida = acaoVencida(acao, hoje);
  const sc = ACAO_STATUS_COLOR[acao.status];
  const concluida = acao.status === 'concluido';

  return (
    <li
      data-vencido={vencida ? 'true' : 'false'}
      className={`rounded-lg border p-2.5 transition ${
        vencida ? 'border-level-emergencia/60 bg-jf-surface2/40' : 'border-jf-border bg-jf-surface2/40'
      } ${concluida ? 'opacity-70' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        {/* Sem riscado: numa concluída o texto É o registro do que foi feito (pedido do dono, 27/09). */}
        <p className={`text-[13px] ${concluida ? 'text-slate-400' : 'text-white'}`}>
          {acao.descricao}
        </p>
        <span
          className="shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-bold"
          style={{ color: sc, borderColor: `${sc}66`, background: `${sc}1a` }}
        >
          {ACAO_STATUS_CARD[acao.status]}
        </span>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
        <span className="text-jf-dim">👤 {acao.responsavel}</span>
        {acao.prazo ? (
          <span className={vencida ? 'font-bold' : 'text-jf-dim'} style={vencida ? { color: '#ef4444' } : undefined}>
            📅 Prazo {formatarDiaBr(acao.prazo)}
            {vencida && ' · vencida'}
          </span>
        ) : (
          <span className="text-jf-dim">📅 sem prazo</span>
        )}
        {concluida && (
          <span className="text-level-normal">
            {acao.concluidoEm ? `Concluída em ${formatarDiaDoInstante(acao.concluidoEm)}` : 'Conclusão sem data registrada'}
          </span>
        )}
      </div>

      {canEdit && !concluida && (
        <div className="mt-2 flex w-fit flex-wrap items-center gap-2">
          {acao.status === 'pendente' && (
            <Button
              variant="ghost"
              onClick={() => onUpdateAcao(acao.planoId, acao.id, 'andamento')}
              disabled={updating}
            >
              Iniciar
            </Button>
          )}
          <Button
            variant="green"
            onClick={() => onUpdateAcao(acao.planoId, acao.id, 'concluido')}
            disabled={updating}
          >
            Concluir
          </Button>
        </div>
      )}
    </li>
  );
}
